import { useMy } from '@/hooks/useMy';
import { useAuth } from '@/state/auth';

export type FreemiumPhase = 1 | 2 | 3;
export type FreemiumPhaseStatus = 'pending' | 'active' | 'complete';

export interface FreemiumOnboardingState {
  isFreemium: boolean;
  phase: FreemiumPhase;
  phaseStatus: FreemiumPhaseStatus;
  complete: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Derive-only freemium onboarding state.
 *
 * There is no stored `freemium_onboarding` record and no write surface: the
 * onboarding phase + completion are computed live from durable server-side
 * signals (assets, risks, hunts). Because those resources persist server-side,
 * the derived state naturally survives reloads and sessions, satisfying the
 * "progress persists" requirement with zero persistence code.
 *
 * `isFreemium` is read synchronously from the already-loaded `/whoami` response
 * rather than an async all-tenants `/my` query, so a freemium owner is
 * classified on the very first render — no window where the real app flashes
 * before the gate holds it. That guarantee is scoped to whoami-loaded renders:
 * during the cold-load window where `whoami` is still undefined, `isFreemium`
 * is false and CheckAuth's owner branch renders the app until whoami resolves
 * (a pre-existing pending-guard gap in CheckAuth, not introduced here).
 * It reads the TENANT's customer type (falling back
 * to the principal's): the backend leaves `principal.customer_type` empty for
 * SSO sign-ins and only fills `tenant.customer_type`, so keying off the
 * principal alone would silently bypass onboarding for SSO freemium tenants.
 * It is additionally scoped to the owner viewing their own tenant
 * (`me === userIdFromUrl`); when a freemium user views a collaborator's tenant
 * the flag is false, so the signal queries never fire against — nor compute
 * progress from — the wrong tenant's data.
 *
 * The three signal queries are gated `enabled: isFreemium` so non-freemium
 * tenants (and non-owner views) fire ZERO extra requests.
 *
 * Error fail-open contract: if any signal query ERRORS, `isError` is true and
 * completion is unknowable — the app-level gate must fail OPEN (render the
 * app) rather than trap an already-onboarded owner in the wizard on a
 * transient API failure.
 */
export function useFreemiumOnboarding(): FreemiumOnboardingState {
  const { me, userIdFromUrl, whoami } = useAuth();

  const isOwnTenant = !!me && me === userIdFromUrl;
  // Prefer the tenant's customer type; the SSO whoami branch leaves the
  // principal's empty. `||` (not `??`) so an empty-string principal/tenant type
  // falls through rather than being treated as a present value.
  const customerType =
    whoami?.tenant?.customer_type || whoami?.principal.customer_type;
  const isFreemium = isOwnTenant && customerType === 'FREEMIUM';

  // Phase 1 signal: any assets enumerated yet. `limit: 1` — this is an
  // existence probe, not a listing, so it must not pull the default page.
  const assets = useMy(
    { resource: 'asset', query: { limit: 1, node: { labels: ['Asset'] } } },
    { enabled: isFreemium }
  );
  const hasAssets = assets.hasFetchedData && assets.data.length > 0;

  // Phase 2 signal: any risks surfaced.
  const risks = useMy(
    { resource: 'risk', query: { limit: 1, node: { labels: ['Risk'] } } },
    { enabled: isFreemium }
  );
  const hasRisks = risks.hasFetchedData && risks.data.length > 0;

  // Phase 3 signal (single completion gate): a hunt resource exists. Hunts are
  // key-prefix `#hunt` resources (not graph-label nodes) and are inherently few
  // per tenant, so the default listing is already cheap.
  const hunts = useMy({ resource: 'hunt' }, { enabled: isFreemium });
  const hasHunt = hunts.hasFetchedData && hunts.data.length > 0;

  const complete = isFreemium && hasHunt;

  let phase: FreemiumPhase;
  let phaseStatus: FreemiumPhaseStatus;
  if (hasHunt) {
    phase = 3;
    phaseStatus = 'complete';
  } else if (hasAssets || hasRisks) {
    phase = 2;
    phaseStatus = 'active';
  } else {
    phase = 1;
    phaseStatus = 'active';
  }

  // With `retry: false` in useMy, a FAILED signal query settles (`isLoading`
  // false) with `hasFetchedData` still false — indistinguishable from "loaded
  // empty" above. Surface errors separately so the gate can fail OPEN on them
  // instead of trapping an already-onboarded owner in the wizard.
  const isError =
    isFreemium && (assets.isError || risks.isError || hunts.isError);

  // `isFreemium` is known synchronously, so loading depends only on the signal
  // queries. For non-freemium tenants this is `false` immediately (the queries
  // are disabled and never fetch), and for a freemium owner it stays `true`
  // until the signals resolve — which is what lets the gate hold the render
  // instead of flashing the app.
  //
  // `isError` takes PRECEDENCE over `isLoading`: once any probe has settled
  // failed (retry: false), completion is already unknowable, so the gate must
  // fail open immediately rather than hold a blank screen waiting on a slow or
  // hung sibling probe. The hook guarantees `isError` and `isLoading` are
  // mutually exclusive so every consumer sees self-consistent state.
  const isLoading =
    isFreemium &&
    !isError &&
    (assets.isLoading || risks.isLoading || hunts.isLoading);

  return {
    isFreemium,
    phase,
    phaseStatus,
    complete,
    isLoading,
    isError,
  };
}
